import React, { useState, useRef, useEffect } from 'react';

interface ResizableSplitterProps {
  left: React.ReactNode;
  right: React.ReactNode;
  initialLeftWidth?: number; // Percentage (0-100)
  minLeftWidth?: number; // Percentage
  maxLeftWidth?: number; // Percentage
}

const ResizableSplitter: React.FC<ResizableSplitterProps> = ({
  left,
  right,
  initialLeftWidth = 30,
  minLeftWidth = 20,
  maxLeftWidth = 60,
}) => {
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startLeftWidthRef = useRef<number>(initialLeftWidth);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const deltaX = e.clientX - startXRef.current;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newLeftWidth = startLeftWidthRef.current + deltaPercent;

      // Clamp between min and max
      const clampedWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, newLeftWidth));
      setLeftWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, minLeftWidth, maxLeftWidth]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;
    startLeftWidthRef.current = leftWidth;
  };

  return (
    <div className="flex w-full h-full overflow-hidden" ref={containerRef}>
      <div className="h-full overflow-hidden flex-shrink-0" style={{ width: `${leftWidth}%` }}>
        {left}
      </div>
      <div
        className="w-1 bg-gray-300 cursor-col-resize flex-shrink-0 relative hover:bg-indigo-500 transition-colors"
        onMouseDown={handleMouseDown}
      >
        <div className="absolute -left-2 -right-2 top-0 bottom-0 cursor-col-resize"></div>
      </div>
      <div className="h-full overflow-hidden flex-1 min-w-0" style={{ width: `${100 - leftWidth}%` }}>
        {right}
      </div>
    </div>
  );
};

export default ResizableSplitter;
